import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import debounce from 'lodash.debounce';
import CloseIcon from '../../../../../assets/CloseIcon.svg';
import AsyncSelectCommon from '../../../../SearchableSelect/AsyncSelect';
import Button from '../../../../Button';
import notification from '../../../../notification';
import useImpersonation from '../../../../../hooks/useImpersonation';
import {
  getImpersonationUsers,
  getProfileDashboardList,
  IMPERSONATION_USER_TYPES
} from '../../../../../utils/api-helper';

const LIST_LIMIT = 15;

const USER_TYPE_OPTIONS = [
  { label: 'Internal', value: IMPERSONATION_USER_TYPES.INTERNAL },
  { label: 'Client', value: IMPERSONATION_USER_TYPES.CLIENT }
];

const selectProps = {
  isClearable: true,
  showDropdownIndicator: true,
  controlStyle: { backgroundColor: '#F3F4F6' },
  menuPortalTarget: document.body,
  menuPortalStyle: { zIndex: 21 }
};

const ImpersonateUserModal = ({ isOpen, handleClose }) => {
  const { startImpersonation } = useImpersonation();
  const [userType, setUserType] = useState(IMPERSONATION_USER_TYPES.INTERNAL);
  const [showProfileFilter, setShowProfileFilter] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const isClient = userType === IMPERSONATION_USER_TYPES.CLIENT;
  const partnerProfileId = isClient ? selectedProfile?.id : null;

  const loadUserOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        getImpersonationUsers({
          userType,
          query: inputValue,
          partnerProfileId,
          limit: LIST_LIMIT
        })
          ?.then((response) => {
            callback(response?.data);
          })
          .catch((error) => {
            notification(
              'error',
              error?.response?.data?.error || 'Failed to fetch users list!'
            );
          });
      }, 500),
    [userType, partnerProfileId]
  );

  const loadProfileOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        getProfileDashboardList({
          filterBy: { partnerType: [], productType: [], state: [] },
          limit: LIST_LIMIT,
          page: 1,
          query: inputValue || ''
        })
          ?.then((response) => {
            callback(response?.data?.result);
          })
          .catch((error) => {
            notification(
              'error',
              error?.response?.data?.error ||
                'Failed to fetch partner profiles!'
            );
          });
      }, 500),
    []
  );

  const handleChangeUserType = (type) => {
    setUserType(type);
    setShowProfileFilter(false);
    setSelectedProfile(null);
    setSelectedUser(null);
  };

  const handleRemoveProfileFilter = () => {
    setShowProfileFilter(false);
    handleChangeProfile(null);
  };

  const handleChangeProfile = (profile) => {
    setSelectedProfile(profile);
    setSelectedUser(null);
  };

  const handleImpersonate = () => {
    if (!selectedUser) {
      notification('warning', 'Please select a user to impersonate');
      return;
    }

    startImpersonation(selectedUser?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      className={
        'flex flex-col relative w-[95%] max-w-[480px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-md'
      }
      overlayClassName={'fixed top-0 left-0 right-0 bottom-0 bg-black/80'}
      ariaHideApp={false}
      onRequestClose={handleClose}
      style={{ overlay: { zIndex: 20 } }}
      shouldCloseOnOverlayClick={true}
    >
      <div className='flex flex-col w-full bg-white rounded-lg p-4 gap-y-4'>
        <div className='flex flex-row justify-between items-center'>
          <span className='text-lg font-medium'>Impersonate User</span>

          <button onClick={handleClose}>
            <img src={CloseIcon} alt='Close' className='w-2.5 h-2.5' />
          </button>
        </div>

        <div className='flex flex-row gap-x-6'>
          {USER_TYPE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className='flex flex-row items-center gap-x-2 text-sm cursor-pointer'
            >
              <input
                type='radio'
                name='impersonateUserType'
                value={option.value}
                checked={userType === option.value}
                onChange={() => handleChangeUserType(option.value)}
                className='accent-primary-500'
              />
              {option.label}
            </label>
          ))}
        </div>

        {isClient && showProfileFilter && (
          <div className='flex flex-col gap-y-1'>
            <div className='flex flex-row justify-between items-center'>
              <span className='text-sm text-neutral-700'>Partner Profile</span>
              <button
                onClick={handleRemoveProfileFilter}
                className='text-xs text-neutral-500 hover:text-neutral-700'
              >
                Remove filter
              </button>
            </div>
            <AsyncSelectCommon
              {...selectProps}
              loadOptions={loadProfileOptions}
              value={selectedProfile}
              onChange={handleChangeProfile}
              getOptionLabel={(profile) => profile?.name}
              getOptionValue={(profile) => profile?.id}
              placeholder='Filter by partner profile'
            />
          </div>
        )}

        <div className='flex flex-col gap-y-1'>
          <div className='flex flex-row justify-between items-center'>
            <span className='text-sm text-neutral-700'>User</span>
            {isClient && !showProfileFilter && (
              <button
                onClick={() => setShowProfileFilter(true)}
                className='text-xs text-primary-500 hover:underline'
              >
                + Filter by partner profile
              </button>
            )}
          </div>
          <AsyncSelectCommon
            {...selectProps}
            key={`${userType}-${partnerProfileId ?? 'all'}`}
            loadOptions={loadUserOptions}
            value={selectedUser}
            onChange={setSelectedUser}
            getOptionLabel={(user) =>
              `${user?.firstName?.trim()} ${user?.lastName?.trim()} (${
                user?.email
              })`
            }
            getOptionValue={(user) => user?.id}
            placeholder='Search by name or email'
          />
        </div>

        <div className='flex flex-row justify-end gap-x-2'>
          <button
            onClick={handleClose}
            className='px-3 py-1.5 rounded text-sm border border-neutral-200 hover:bg-neutral-100'
          >
            Cancel
          </button>

          <Button
            handleClick={handleImpersonate}
            title={'Impersonate'}
            disabled={!selectedUser}
            buttonStyle={`!bg-primary-500 !p-1.5 !px-3 ${
              selectedUser ? '' : 'opacity-50 cursor-not-allowed'
            }`}
            textStyle={'text-sm'}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ImpersonateUserModal;
